import React, { useState, useEffect, useRef } from 'react';
import { 
  collection, addDoc, getDocs, query, orderBy, Timestamp, limit
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { 
  Send, Users, Smartphone, Bell, CheckCircle2, AlertCircle, Clock, Target,
  ChevronDown, Image as ImageIcon, LayoutTemplate, Upload, X, Loader2,
  Search, ShoppingBag, BookOpen, GraduationCap, Package, ArrowRight,
} from 'lucide-react';

interface NotificationCampaign {
  id: string;
  title: string;
  body: string;
  imageUrl?: string;
  target: string;
  topic?: string;
  targetScreen?: string;
  targetId?: string;
  data?: Record<string, any>;
  status: 'pending' | 'sent' | 'failed';
  sentCount?: number;
  failedCount?: number;
  createdAt: Timestamp;
}

interface TargetOption { value: string; label: string; icon: React.ReactNode; description: string; }

// ─── Deeplink Screens ────────────────────────────────────────────────────────
interface DeeplinkScreen {
  value: string;
  label: string;
  icon: React.ReactNode;
  requiresId: boolean;
  collection?: string; // Firestore collection to pick from
  idField?: string;    // field to use as ID
  labelField?: string; // field to show as name
}

const deeplinkScreens: DeeplinkScreen[] = [
  { value: '', label: 'No Navigation (Default)', icon: <Bell className="w-4 h-4" />, requiresId: false },
  { value: 'home', label: 'Home Screen', icon: <LayoutTemplate className="w-4 h-4" />, requiresId: false },
  { value: 'store', label: 'Store (All Products)', icon: <ShoppingBag className="w-4 h-4" />, requiresId: false },
  { 
    value: 'productDetail', label: 'Product Detail', icon: <Package className="w-4 h-4" />, 
    requiresId: true, collection: 'shopProducts', idField: 'id', labelField: 'name'
  },
  { value: 'articleList', label: 'Article List', icon: <BookOpen className="w-4 h-4" />, requiresId: false },
  { 
    value: 'articleDetail', label: 'Article Detail', icon: <BookOpen className="w-4 h-4" />,
    requiresId: true, collection: 'articles', idField: 'id', labelField: 'title'
  },
  { value: 'classList', label: 'Class List', icon: <GraduationCap className="w-4 h-4" />, requiresId: false },
  { 
    value: 'classDetail', label: 'Class Detail', icon: <GraduationCap className="w-4 h-4" />,
    requiresId: true, collection: 'classes', idField: 'id', labelField: 'title'
  },
  { value: 'health', label: 'Health Dashboard', icon: <Target className="w-4 h-4" />, requiresId: false },
  { value: 'profile', label: 'Profile', icon: <Users className="w-4 h-4" />, requiresId: false },
  { value: 'settings', label: 'Settings', icon: <Smartphone className="w-4 h-4" />, requiresId: false },
];

const targetOptions: TargetOption[] = [
  { value: 'all', label: 'All Users', icon: <Users className="w-5 h-5" />, description: 'Send to all Android and iOS devices' },
  { value: 'android', label: 'Android Only', icon: <Smartphone className="w-5 h-5" />, description: 'Send only to Android devices' },
  { value: 'ios', label: 'iOS Only', icon: <Smartphone className="w-5 h-5" />, description: 'Send only to iOS devices' },
  { value: 'authenticated', label: 'Authenticated Users', icon: <CheckCircle2 className="w-5 h-5" />, description: 'Send to logged-in users only' },
  { value: 'guests', label: 'Guest Users', icon: <Users className="w-5 h-5" />, description: 'Send to guest/anonymous users' },
  { value: 'topic', label: 'Topic Subscribers', icon: <Bell className="w-5 h-5" />, description: 'Send to users subscribed to a specific topic' },
];

// ─── Firestore Item Picker ────────────────────────────────────────────────────
function FirestorePicker({ 
  collectionName, labelField, value, onChange 
}: { 
  collectionName: string; labelField: string;
  value: string; onChange: (id: string, label: string) => void; 
}) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    getDocs(query(collection(db, collectionName), limit(100)))
      .then(snap => setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [collectionName]);

  const filtered = items.filter(item => 
    String(item[labelField] || item.name || item.title || item.id)
      .toLowerCase().includes(search.toLowerCase())
  );

  const selectedItem = items.find(i => i.id === value);
  const selectedLabel = selectedItem ? (selectedItem[labelField] || selectedItem.name || selectedItem.title || selectedItem.id) : '';

  return (
    <div className="space-y-2">
      {/* Selected indicator */}
      {value && (
        <div className="flex items-center justify-between p-2.5 rounded-xl bg-orange-500/10 border border-orange-500/30">
          <span className="text-sm text-orange-300 font-medium">{selectedLabel}</span>
          <code className="text-xs text-orange-400 font-mono">{value}</code>
        </div>
      )}
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-stone-500" />
        <input
          type="text"
          placeholder={`Cari ${collectionName}...`}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full bg-[#1A1A1D] border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-stone-500 focus:outline-none focus:border-orange-500/50"
        />
      </div>
      {/* Items */}
      <div className="max-h-40 overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5">
        {loading ? (
          <div className="p-4 text-center"><Loader2 className="w-5 h-5 text-orange-500 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-center text-sm text-stone-500">Tidak ada data ditemukan</div>
        ) : filtered.map(item => {
          const label = item[labelField] || item.name || item.title || item.id;
          const isSelected = item.id === value;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onChange(item.id, label)}
              className={`w-full px-4 py-2.5 flex items-center justify-between text-left transition hover:bg-white/5 ${isSelected ? 'bg-orange-500/10' : ''}`}
            >
              <span className="text-sm text-white truncate">{label}</span>
              {isSelected && <CheckCircle2 className="w-4 h-4 text-orange-400 shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export function PushNotifications() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [target, setTarget] = useState('all');
  const [topic, setTopic] = useState('');
  const [targetScreen, setTargetScreen] = useState('');
  const [targetId, setTargetId] = useState('');
  const [targetLabel, setTargetLabel] = useState('');
  const [campaigns, setCampaigns] = useState<NotificationCampaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showTargetDropdown, setShowTargetDropdown] = useState(false);
  
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedScreen = deeplinkScreens.find(s => s.value === targetScreen);

  useEffect(() => { loadCampaigns(); }, []);

  // Reset targetId when screen changes
  useEffect(() => { setTargetId(''); setTargetLabel(''); }, [targetScreen]);

  const loadCampaigns = async () => {
    setIsLoading(true);
    try {
      const q = query(collection(db, 'notificationCampaigns'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      setCampaigns(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as NotificationCampaign[]);
    } catch (error) { console.error('Error loading campaigns:', error); }
    finally { setIsLoading(false); }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setUploadError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('Image size must be less than 5MB'); return; }
    
    setIsUploading(true); setUploadProgress(0); setUploadError('');
    try {
      const storageRef = ref(storage, `notifications/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on('state_changed',
        (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        (err) => { setUploadError('Failed to upload image'); setIsUploading(false); console.error(err); },
        async () => { setImageUrl(await getDownloadURL(uploadTask.snapshot.ref)); setIsUploading(false); }
      );
    } catch { setUploadError('Failed to upload image'); setIsUploading(false); }
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) { alert('Title and body are required'); return; }
    if (target === 'topic' && !topic.trim()) { alert('Topic is required'); return; }
    if (selectedScreen?.requiresId && !targetId) { alert('Pilih item tujuan deeplink terlebih dahulu'); return; }

    setIsSending(true);
    try {
      const data: Record<string, any> = {};
      if (targetScreen) data.targetScreen = targetScreen;
      if (targetId) data.targetId = targetId;
      if (targetLabel) data.targetLabel = targetLabel;

      const payload: any = {
        type: 'campaign',
        target,
        title: title.trim(),
        body: body.trim(),
        status: 'pending',
        createdAt: Timestamp.now(),
        ...(imageUrl.trim() && { imageUrl: imageUrl.trim() }),
        ...(target === 'topic' && { topic: topic.trim() }),
        ...(Object.keys(data).length > 0 && { data }),
      };

      await addDoc(collection(db, 'notificationCampaigns'), payload);
      alert('✅ Campaign berhasil dibuat! Firebase Function akan mengirim notifikasi.');
      setTitle(''); setBody(''); setImageUrl(''); setTopic(''); setTargetScreen(''); setTargetId(''); setTargetLabel('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadCampaigns();
    } catch { alert('Failed to create campaign'); }
    finally { setIsSending(false); }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'failed': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
  };

  const getTargetLabel = (value: string) => targetOptions.find(opt => opt.value === value)?.label || value;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-[36px] border border-orange-500/20 bg-gradient-to-br from-stone-900 to-[#0a0a0a] p-8">
        <div className="inline-flex rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1 text-xs uppercase tracking-[0.24em] text-orange-400">
          Broadcast
        </div>
        <h2 className="mt-5 max-w-2xl text-4xl font-semibold leading-tight text-white flex items-center gap-3">
          <Bell className="w-8 h-8 text-orange-500" />
          Push Notifications
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-7 text-stone-400">
          Kirim notifikasi push ke user dengan deeplink ke halaman tertentu di aplikasi.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* ─── Form ─────────────────────────────────────────── */}
        <div className="space-y-4">
          {/* Compose */}
          <div className="bg-[#1A1A1D] rounded-2xl p-6 border border-white/10 space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2 text-white">
              <Send className="w-5 h-5 text-orange-500" /> Compose Notification
            </h2>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-stone-300 mb-1.5">Notification Title *</label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Promo Akhir Pekan! 🎉"
                className="w-full bg-[#121214] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-stone-500 focus:outline-none focus:border-orange-500/50 transition"
                maxLength={100} />
              <p className="text-xs text-stone-600 mt-1 text-right">{title.length}/100</p>
            </div>

            {/* Body */}
            <div>
              <label className="block text-sm font-medium text-stone-300 mb-1.5">Message Body *</label>
              <textarea value={body} onChange={e => setBody(e.target.value)}
                placeholder="Tulis pesan notifikasi di sini..."
                rows={3}
                className="w-full bg-[#121214] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-stone-500 focus:outline-none focus:border-orange-500/50 transition resize-none"
                maxLength={200} />
              <p className="text-xs text-stone-600 mt-1 text-right">{body.length}/200</p>
            </div>

            {/* Image */}
            <div>
              <label className="block text-sm font-medium text-stone-300 mb-1.5 flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> Gambar Notifikasi (Opsional)
              </label>
              {imageUrl ? (
                <div className="relative rounded-xl overflow-hidden bg-[#121214] border border-white/10">
                  <img src={imageUrl} alt="Notification" className="w-full h-36 object-cover" />
                  <button onClick={() => { setImageUrl(''); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="absolute top-2 right-2 p-1.5 bg-black/70 hover:bg-red-500 rounded-lg transition">
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ) : (
                <div onClick={() => fileInputRef.current?.click()}
                  className="w-full bg-[#121214] border-2 border-dashed border-white/20 rounded-xl px-4 py-6 text-center cursor-pointer hover:border-orange-500/50 transition">
                  {isUploading ? (
                    <div className="space-y-2">
                      <Loader2 className="w-6 h-6 text-orange-500 mx-auto animate-spin" />
                      <div className="w-full bg-stone-700 rounded-full h-1.5 max-w-xs mx-auto">
                        <div className="bg-orange-500 h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                      </div>
                      <p className="text-xs text-stone-400">{uploadProgress}%</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-stone-500 mx-auto mb-1" />
                      <p className="text-sm text-stone-400">Klik untuk upload gambar</p>
                      <p className="text-xs text-stone-600 mt-0.5">JPG, PNG, GIF (max 5MB)</p>
                    </>
                  )}
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
              {uploadError && <p className="text-sm text-red-400 mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{uploadError}</p>}
            </div>
          </div>

          {/* Target Audience */}
          <div className="bg-[#1A1A1D] rounded-2xl p-6 border border-white/10 space-y-4">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Target className="w-4 h-4 text-orange-500" /> Target Audience
            </h3>
            <div className="relative">
              <button
                onClick={() => setShowTargetDropdown(!showTargetDropdown)}
                className="w-full bg-[#121214] border border-white/10 rounded-xl px-4 py-3 text-left flex items-center justify-between hover:border-orange-500/30 transition"
              >
                <span className="flex items-center gap-3 text-white">
                  {targetOptions.find(opt => opt.value === target)?.icon}
                  {getTargetLabel(target)}
                </span>
                <ChevronDown className={`w-5 h-5 text-stone-400 transition-transform ${showTargetDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showTargetDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-[#1A1A1D] border border-white/10 rounded-xl shadow-xl overflow-hidden">
                  {targetOptions.map(opt => (
                    <button key={opt.value}
                      onClick={() => { setTarget(opt.value); setShowTargetDropdown(false); }}
                      className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-white/5 transition text-left ${target === opt.value ? 'bg-orange-500/10' : ''}`}
                    >
                      <div className="mt-0.5 text-orange-500">{opt.icon}</div>
                      <div>
                        <p className="font-medium text-white text-sm">{opt.label}</p>
                        <p className="text-xs text-stone-500">{opt.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {target === 'topic' && (
              <div>
                <label className="block text-sm font-medium text-stone-300 mb-1.5">Topic Name *</label>
                <input type="text" value={topic} onChange={e => setTopic(e.target.value)}
                  placeholder="e.g. premium_users, new_features"
                  className="w-full bg-[#121214] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-stone-500 focus:outline-none focus:border-orange-500/50 transition" />
              </div>
            )}
          </div>

          {/* ─── Deeplink Section ─────────────────────────────── */}
          <div className="bg-[#1A1A1D] rounded-2xl p-6 border border-white/10 space-y-4">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <LayoutTemplate className="w-4 h-4 text-orange-500" /> Deeplink Destination
              <span className="text-xs font-normal text-stone-500 ml-1">(opsional)</span>
            </h3>

            {/* Screen picker */}
            <div>
              <label className="block text-sm font-medium text-stone-300 mb-1.5">Navigate to Screen</label>
              <div className="grid grid-cols-2 gap-2">
                {deeplinkScreens.map(screen => (
                  <button
                    key={screen.value}
                    type="button"
                    onClick={() => setTargetScreen(screen.value)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition text-left ${
                      targetScreen === screen.value
                        ? 'border-orange-500 bg-orange-500/10 text-orange-300'
                        : 'border-white/10 bg-[#121214] text-stone-400 hover:border-white/20 hover:text-white'
                    }`}
                  >
                    <span className={targetScreen === screen.value ? 'text-orange-400' : 'text-stone-500'}>
                      {screen.icon}
                    </span>
                    <span className="truncate">{screen.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Live data picker when screen requires an ID */}
            {selectedScreen?.requiresId && selectedScreen.collection && (
              <div>
                <label className="block text-sm font-medium text-stone-300 mb-1.5">
                  Pilih {selectedScreen.label.replace(' Detail', '')} *
                </label>
                <FirestorePicker
                  collectionName={selectedScreen.collection}
                  labelField={selectedScreen.labelField || 'title'}
                  value={targetId}
                  onChange={(id, label) => { setTargetId(id); setTargetLabel(label); }}
                />
              </div>
            )}

            {/* Deeplink summary */}
            {targetScreen && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-stone-900 border border-stone-800 text-xs">
                <ArrowRight className="w-3 h-3 text-orange-400 shrink-0" />
                <span className="text-stone-400">Deeplink:</span>
                <code className="text-orange-300 font-mono">
                  {targetScreen}{targetId ? `/${targetId}` : ''}
                </code>
              </div>
            )}
          </div>

          {/* Send Button */}
          <button
            onClick={handleSend}
            disabled={isSending || !title.trim() || !body.trim() || isUploading}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-2xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-orange-500/20"
          >
            {isSending ? (
              <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Mengirim...</>
            ) : (
              <><Send className="w-5 h-5" />Kirim Notifikasi</>
            )}
          </button>
        </div>

        {/* ─── Preview + History ─────────────────────────────── */}
        <div className="space-y-4">
          {/* Preview */}
          {(title || body) && (
            <div className="bg-[#1A1A1D] rounded-2xl p-6 border border-white/10">
              <h3 className="text-sm font-medium text-stone-400 mb-4">Preview Notifikasi</h3>
              <div className="bg-[#121214] rounded-2xl p-4 border border-white/5">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Bell className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-white truncate">{title || 'Notification Title'}</h4>
                    <p className="text-sm text-stone-400 mt-1 line-clamp-2">{body || 'Notification message...'}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <p className="text-xs text-stone-600">Now</p>
                      {targetScreen && (
                        <span className="text-xs text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded-full">
                          → {selectedScreen?.label}
                          {targetLabel ? `: ${targetLabel}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {imageUrl && (
                  <div className="mt-3 rounded-xl overflow-hidden">
                    <img src={imageUrl} alt="Preview" className="w-full h-32 object-cover"
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Campaign History */}
          <div className="bg-[#1A1A1D] rounded-2xl border border-white/10 overflow-hidden">
            <div className="p-5 border-b border-white/10">
              <h2 className="text-base font-semibold flex items-center gap-2 text-white">
                <Clock className="w-4 h-4 text-orange-500" /> Riwayat Campaign
              </h2>
            </div>
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto" />
                <p className="text-stone-400 mt-3 text-sm">Loading...</p>
              </div>
            ) : campaigns.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-stone-700 mx-auto mb-3" />
                <p className="text-stone-400 text-sm">Belum ada campaign</p>
              </div>
            ) : (
              <div className="max-h-[500px] overflow-y-auto divide-y divide-white/5">
                {campaigns.map(c => (
                  <div key={c.id} className="p-4 hover:bg-white/5 transition">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white text-sm truncate">{c.title}</h3>
                        <p className="text-xs text-stone-400 mt-0.5 line-clamp-2">{c.body}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-stone-500 flex-wrap">
                          <span className="flex items-center gap-1"><Target className="w-3 h-3" />{getTargetLabel(c.target)}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{c.createdAt.toDate().toLocaleDateString('id-ID')}</span>
                          {c.data?.targetScreen && (
                            <span className="text-orange-400/80">→ {c.data.targetScreen}{c.data.targetId ? `/${c.data.targetId}` : ''}</span>
                          )}
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${getStatusColor(c.status)}`}>
                        {c.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-5">
            <h3 className="font-semibold text-blue-300 mb-2 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Tips Deeplink
            </h3>
            <ul className="space-y-1.5 text-xs text-stone-400">
              <li>• <strong className="text-stone-300">Product/Article/Class Detail</strong> — pilih item di picker untuk auto-fill ID</li>
              <li>• <strong className="text-stone-300">data.targetScreen</strong> dikirim ke mobile via FCM data payload</li>
              <li>• <strong className="text-stone-300">data.targetId</strong> digunakan mobile untuk navigasi ke detail yang tepat</li>
              <li>• Pastikan screen handler sudah diimplementasikan di mobile app</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
